import { Trans, useLingui } from "@lingui/react/macro";
import { useFormContext } from "react-hook-form";
import { FilledButton } from "@medoma/components/Button/Button";
import { Loading } from "@medoma/components/Loading/Loading";
import Form from "@/components/Form/Form";
import type { IActivityCategory } from "@medoma/models/activities";
import { calendarDateSchema } from "@medoma/models/date-and-time";
import type { IPatientId } from "@medoma/models/patients";
import { convertDurationToMinutes } from "@/forms/AddActivityForm/activityDurationUtils";
import { ActivityFields } from "./ActivityFields";
import type { IRegisterActivitiesRetroactivelyFormData } from "./RegisterActivityRetroactivelyForm";
import { useSubmitRetroactively } from "./useSubmitRetroactively";

export const SingleRetroForm = ({
  patientId,
  category,
}: {
  patientId: IPatientId;
  category: IActivityCategory;
}) => {
  const { t } = useLingui();
  const { handleSubmit } =
    useFormContext<IRegisterActivitiesRetroactivelyFormData>();
  const { mutate, isPending } = useSubmitRetroactively({ patientId });

  const validateAndSubmit = handleSubmit((formData) => {
    const [firstActivity] = formData.activities;
    if (!firstActivity) {
      return;
    }

    mutate({
      submission: {
        kind: "single",
        activity: {
          activityId: firstActivity.activityId,
          careEpisodeId: patientId,
          title: firstActivity.title.trim(),
          date: calendarDateSchema.decode(formData.startDate),
          time: formData.time,
          category,
          duration: convertDurationToMinutes(
            firstActivity.durationOption,
            firstActivity.customDuration,
            firstActivity.customDurationUnit,
          ),
          shiftId: formData.shiftId,
          description: firstActivity.description?.trim() || undefined,
        },
      },
      activities: formData.activities,
    });
  });

  if (isPending) {
    return <Loading message={t`Efterregistrerar`} />;
  }

  return (
    <Form onSubmit={validateAndSubmit}>
      <ActivityFields
        patientId={patientId}
        category={category}
        namePrefix="activities.0"
      />
      <FilledButton type="submit">
        <Trans>Efterregistrera aktiviteten</Trans>
      </FilledButton>
    </Form>
  );
};
